import React, { useState, useEffect } from "react";
import Select from "react-select";

interface SearchableSelectProps {
  value: any;
  onChange: (value: any) => void;
  onSearch: (searchTerm: string) => Promise<any[] | any>;
  placeholder?: string;
  className?: string;
  classNamePrefix?: string;
  isClearable?: boolean;
  isDisabled?: boolean;
  minSearchLength?: number;
  itemsPerPage?: number;
  formatOptionLabel?: (item: any) => { label: string; value: any };
  noOptionsMessage?: string;
  minSearchMessage?: string;
  loadingMessage?: string;
}

const SearchableSelect: React.FC<SearchableSelectProps> = ({
  value,
  onChange,
  onSearch,
  placeholder = "Pilih...",
  className = "basic-multi-select",
  classNamePrefix = "select",
  isClearable = true,
  isDisabled = false,
  minSearchLength = 2,
  itemsPerPage = 5,
  formatOptionLabel,
  noOptionsMessage = "Tidak ada data",
  minSearchMessage,
  loadingMessage = "Mencari data...",
}) => {
  const [options, setOptions] = useState<any[]>([]);
  const [allData, setAllData] = useState<any[]>([]);
  const [displayCount, setDisplayCount] = useState(itemsPerPage);
  const [loading, setLoading] = useState(false);
  const [lastSearchTerm, setLastSearchTerm] = useState("");

  const defaultMinSearchMessage = `Ketik minimal ${minSearchLength} huruf untuk mencari`;

  const handleSearch = async (searchTerm: string) => {
    if (searchTerm.length < minSearchLength) {
      setOptions([]);
      setAllData([]);
      setDisplayCount(itemsPerPage);
      setLastSearchTerm("");
      return;
    }

    if (searchTerm === lastSearchTerm) {
      return;
    }

    setLoading(true);
    setLastSearchTerm(searchTerm);

    try {
      const results = await onSearch(searchTerm);

      const data = results?.data || results;

      const arrayData = Array.isArray(data) ? data : [];

      const formattedResults = formatOptionLabel
        ? arrayData.map(formatOptionLabel)
        : arrayData;

      setAllData(formattedResults);
      setOptions(formattedResults.slice(0, itemsPerPage));
      setDisplayCount(itemsPerPage);
    } catch (error) {
      console.error("Search error:", error);
      setAllData([]);
      setOptions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleLoadMore = () => {
    const newCount = displayCount + itemsPerPage;
    setOptions(allData.slice(0, newCount));
    setDisplayCount(newCount);
  };

  const handleInputChange = (inputValue: string, { action }: any) => {
    if (action === "input-change") {
      setDisplayCount(itemsPerPage);
      handleSearch(inputValue);
    }
  };

  const customNoOptionsMessage = ({ inputValue }: any) => {
    if (inputValue.length < minSearchLength) {
      return minSearchMessage || defaultMinSearchMessage;
    }
    return noOptionsMessage;
  };

  const customLoadingMessage = () => loadingMessage;

  const MenuList = (props: any) => {
    const { children, selectProps } = props;
    const { inputValue } = selectProps;
    const hasMore = displayCount < allData.length;

    return (
      <div>
        {children}
        {hasMore && inputValue.length >= minSearchLength && (
          <div
            style={{
              padding: "8px 12px",
              textAlign: "center",
              cursor: "pointer",
              color: "#0ab39c",
              borderTop: "1px solid #e9ebec",
              fontWeight: 500,
              backgroundColor: "#f9f9f9",
            }}
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleLoadMore();
            }}
          >
            Muat Lebih Banyak ({allData.length - displayCount} lagi)
          </div>
        )}
      </div>
    );
  };

  useEffect(() => {
    if (!value) {
      setOptions([]);
      setAllData([]);
      setDisplayCount(itemsPerPage);
      setLastSearchTerm("");
    }
  }, [value, itemsPerPage]);

  return (
    <Select
      value={value}
      onChange={onChange}
      onInputChange={handleInputChange}
      options={options}
      components={{ MenuList }}
      isLoading={loading}
      isDisabled={isDisabled}
      className={className}
      classNamePrefix={classNamePrefix}
      placeholder={placeholder}
      noOptionsMessage={customNoOptionsMessage}
      loadingMessage={customLoadingMessage}
      isClearable={isClearable}
      filterOption={null}
    />
  );
};

export default SearchableSelect;
